// Copyright (c) 2026, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_NAME } from '@src/config/config';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useAuthContext } from '@asgardeo/auth-react';
import type { DecodedIDTokenPayload } from '@asgardeo/auth-react';
import {
  AppBar,
  Avatar,
  Box,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import wso2LogoBlack from '@src/assets/images/wso2-logo-black.svg';
import wso2LogoWhite from '@src/assets/images/wso2-logo-white.svg';

const Header = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { state, signOut, getDecodedIDToken } = useAuthContext();
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );
  const [jobTitle, setJobTitle] = useState<string>('');
  const [profilePicture, setProfilePicture] = useState<string>('');

  const displayName = state.displayName || state.username || '';
  const initial = displayName.charAt(0).toUpperCase();

  useEffect(() => {
    if (!state.isAuthenticated) return;

    let cancelled = false;

    (async () => {
      try {
        const token = await getDecodedIDToken() as DecodedIDTokenPayload;
        if (cancelled) return;

        setJobTitle(typeof token.jobtitle === 'string' ? token.jobtitle : '');
        setProfilePicture(typeof token.profile === 'string' ? token.profile : '');
      } catch {
        // fall back to initial letter
      }
    })();

    return () => { cancelled = true; };
  }, [state.isAuthenticated, getDecodedIDToken]);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        color: (theme) =>
          theme.palette.mode === 'light'
            ? theme.palette.primary.main
            : theme.palette.common.white,
        background: (theme) =>
          theme.palette.mode === 'light'
            ? theme.palette.common.white
            : theme.palette.primary.dark,
        boxShadow: 2,
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          paddingY: 0.3,
          '&.MuiToolbar-root': {
            pl: 0.3,
          },
        }}
      >
        <img
          alt="wso2"
          style={{
            height: '40px',
            maxWidth: '100px',
            padding: '3px',
            cursor: 'pointer',
          }}
          onClick={() => navigate('/')}
          src={theme.palette.mode === 'light' ? wso2LogoBlack : wso2LogoWhite}
        />
        <Typography variant="h5" sx={{ ml: 1, flexGrow: 1, fontWeight: 600 }}>
          {APP_NAME}
        </Typography>

        <Box sx={{ flexGrow: 0 }}>
          {state.isAuthenticated && (
            <>
              <Stack flexDirection="row" alignItems="center" gap={2}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {displayName}
                  </Typography>
                  {jobTitle && (
                    <Typography variant="body2">{jobTitle}</Typography>
                  )}
                </Box>
                <Tooltip title="Account menu">
                  <Avatar
                    onClick={handleOpenUserMenu}
                    sx={{ border: 1, borderColor: 'primary.main' }}
                    src={profilePicture || undefined}
                    alt={displayName || 'Avatar'}
                  >
                    {initial}
                  </Avatar>
                </Tooltip>
              </Stack>

              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem
                  key="logout"
                  onClick={() => {
                    handleCloseUserMenu();
                    signOut();
                  }}
                >
                  <Typography textAlign="center">Logout</Typography>
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
